
import { Component, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter } from 'rxjs';
import { UserService } from '../common/services/user/user.service';
import { ResponsiveDesignService, ScreenType } from '../common/services/responsive-design/responsive-design.service';
import { fadeInOut } from '../common/services/animation/animation';
import { ExternalSettingsService } from '../common/services/external-settings/external-settings.service';
import { Banner } from '../common/components/me-marketing-banner/me-marketing-banner-bean';
import { MEErrorService } from '../common/error/me-error.service';
import { ExternalSettings } from '../common/services/external-settings/external-settings.interface';
import { UIUtilityService } from '../system/utility/ui-utility.service';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { NavigationEnd, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AnimationService } from '../common/services/animation/animation.service';
import { IonContent } from '@ionic/angular';
import { SkinService } from '../system/utility/skin.service';
import { AppMode, AppModeService } from '../system/utility/app-mode.service';

@Component({
    selector: 'app-root',
    templateUrl: './root.component.html',
    styleUrls: ['./root.component.less'],
    animations: [
        fadeInOut("0.4s ease-in")
    ],
    standalone: false
})
export class RootComponent {
  @ViewChild('content', { static: false }) content!: IonContent;
  protected AppMode = AppMode;

  protected loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected banner: BehaviorSubject<Banner | null> = new BehaviorSubject<Banner | null>(null);

  protected hideSidebar: boolean = false;
  protected showBackgroundOverlay: boolean = false;
  protected menuType: string = "left";
  protected hideFooter: boolean = false;
  protected externalSettings!: ExternalSettings;
  protected webBeaconUrl: SafeResourceUrl | undefined = undefined;

  protected PlatformService: any = PlatformService;
  protected UIUtilityService: any = UIUtilityService;
  protected SkinService: any = SkinService;

  constructor(
    private router: Router,
    public srvError: MEErrorService,
    private srvUser: UserService,
    public srvRpDesign: ResponsiveDesignService,
    private srvEs: ExternalSettingsService,
    protected srvAppMode: AppModeService,
    private sanitizer : DomSanitizer) {

    this.srvUser.isLoggedIn$.subscribe((isLoggedIn: boolean) => {
      this.loggedIn.next(isLoggedIn);

      // Handle Special Page
      this.handleSpecificPage();
    })

    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      distinctUntilChanged(),
    ).subscribe((nav: NavigationEnd) => {
      this.handleSpecificPage();
      AnimationService.scrollToTop();
    });

    // Current we only support Portrait mode
    if (PlatformService.isMobile()) {
      ScreenOrientation.lock({ orientation: 'portrait' });
    }
  }

  /**
   * This method is required to revamp in the future. Handle with care
   */
  ngOnInit(): void {
    UIUtilityService.applyEnvClass();

    this.srvRpDesign.screenType.subscribe((st) => {
      this.checkLayout(st);
    })

    this.srvRpDesign.getSidebarNav().subscribe((sn) => {
      if (sn) {
        this.showBackgroundOverlay = true;
      }
    })

    this.srvEs.externalSettings$.subscribe((es) => {
      if (!es) return;
      this.externalSettings = es;
      this.menuType = es.globalVariables.customerWebMenuStyle;
      this.webBeaconUrl = es.webBeaconUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(es.webBeaconUrl) : undefined;
      if (es.web.bannerCustomSide) {
        this.banner.next(es.web.bannerCustomSide);
      }
      this.hideFooter = false;
    })

    combineLatest([this.srvRpDesign.isMobile$, this.srvRpDesign.isTablet$]).subscribe(([isMobile, isTablet]) => {
      if (isMobile || isTablet) {
        if (SkinService.isSkin_2() || SkinService.isSkin_3() || SkinService.isSkin_4()) {
          this.hideFooter = true;
          return;
        }
      }
    })
  }

  ionViewDidEnter() {
    if (this.content) {
      AnimationService.registerIonContent(this.content);
    }
  }

  protected dismissSidebarNav(): void {
    this.srvRpDesign.setSidebarNav(false);
    this.showBackgroundOverlay = false;
  }

  protected getMobileViewBackgroundStyle(): {} {
    let url = this.router.url;
    // if (!UIUtilityService.useSystemMobileViewLoginBackground()) return UIUtilityService.getCustomizedMobileViewLoginBackgroundStyle();
    return (UserService.isLoginPage(url)) ? UIUtilityService.getMobileViewLoginBackgroundStyle() : UIUtilityService.getBackgroundStyle();
  }

  private checkLayout(screenType: ScreenType) {
    switch (screenType) {
      case ScreenType.TypeXS:
      case ScreenType.TypeS:
        this.hideSidebar = true;
        break;
      default:
        this.hideSidebar = false;
        break;
    }
  }

  /**
   * This method is to handle specific page that need to be redirected
   */
  private handleSpecificPage() {
    let url = this.router.url;

    // Redirect to Dashboard if user is logged in and in PreLogon page
    if (this.srvUser.isLoggedIn() && UserService.isPreLogon(url)) {
      this.router.navigate(["/dashboard"]);
      return;
    }
  }

}
